import useSeo from "../lib/useSeo";

const SITE_URL = process.env.PUBLIC_URL;
const ASSET_URL = process.env.PUBLIC_URL || "";

export default function Services() {
  useSeo({
    title: "Liturgical Restoration Services | Evon Polishing",
    description:
      "Specialized restoration services for Catholic liturgical metalware including sacred vessels, polishing, gold and silver plating, preservation, kneeler upholstery, and repair.",
    canonical: `${SITE_URL}/services`,
  });

  return (
    <>
      {/* ================= INTRO ================= */}
      <section className="section paper">
        <div className="container narrow">
          <header className="section-head center">
            <h1>Restoration Services</h1>
            <p className="muted">
              Evon Polishing provides specialized restoration services for
              Catholic liturgical metalware and furnishings. Our work is guided
              by reverence for sacred use, respect for original craftsmanship,
              and a conservative approach to preservation.
            </p>
          </header>

          <div className="prose">
            <p>
              Each item is evaluated individually to determine condition,
              materials, prior finishes, and the most appropriate course of
              action. Restoration is performed with restraint, avoiding
              unnecessary alteration while restoring clarity, dignity, and
              function.
            </p>
          </div>
        </div>
      </section>

      {/* ================= SACRED VESSELS ================= */}
      <section className="section stone">
        <div className="container narrow">

          <h2>Sacred Vessels</h2>
          <div className="service-image-row">
            <img src={`${ASSET_URL}/images/services/chalice-a.jpg`} alt="Chalice restoration detail" />
            <img src={`${ASSET_URL}/images/services/chalice-b.jpg`} alt="Ciborium restoration detail" />
            <img src={`${ASSET_URL}/images/services/ciboria.jpg`} alt="Paten restoration detail" />
          </div>
          <p className="muted">Chalices, Ciboria, and Patens</p>

          <div className="prose">
            <p>
              Sacred Vessels are those items directly used for the celebration
              of Holy Communion. Restoration of these vessels is approached
              with particular care, recognizing both their liturgical
              significance and the materials from which they are made.
            </p>

            <p>
              We restore chalices, ciboria, and patens with attention to
              surface condition, finish integrity, and original detail. Work
              may include controlled polishing, correction of surface wear,
              replating of worn gold or silver surfaces, and preservation of
              inscriptions or ornamentation.
            </p>

            <p>
              All restoration is performed with the goal of maintaining the
              vessel’s original proportions, detail, and dignity, while
              ensuring suitability for continued liturgical use.
            </p>
          </div>
        </div>
      </section>

      {/* ================= POLISHING ================= */}
      <section className="section paper">
        <div className="container narrow">

          <h2>Polishing</h2>
          <div className="service-image-row">
            <img src={`${ASSET_URL}/images/services/sanctuary.jpg`} alt="Metal polishing process" />
            <img src={`${ASSET_URL}/images/services/altar-candles.jpg`} alt="Surface refinement detail" />
            <img src={`${ASSET_URL}/images/services/easter-candle.jpg`} alt="Engraving preservation detail" />
          </div>
          <p className="muted">Controlled Surface Refinement</p>

          <div className="prose">
            <p>
              Polishing services address tarnish, oxidation, surface dullness,
              and minor wear. Our approach emphasizes preservation rather than
              aggressive material removal.
            </p>

            <p>
              Over-polishing, which can soften detail and alter form, is
              intentionally avoided. Each surface is treated according to its
              material, finish, and condition, with careful attention paid to
              edges, engravings, and decorative elements.
            </p>

            <p>
              Polishing may be performed as a standalone service or as part of
              a broader restoration scope.
            </p>
          </div>
        </div>
      </section>

      {/* ================= GOLD & SILVER PLATING ================= */}
      <section className="section stone">
        <div className="container narrow">

          <h2>Gold &amp; Silver Plating</h2>
          <div className="service-image-row">
            <img src={`${ASSET_URL}/images/services/inside-chalice.jpg`} alt="Gold plating interior detail" />
            <img src={`${ASSET_URL}/images/services/open-ciboria.jpg`} alt="Silver plating surface detail" />
            <img src={`${ASSET_URL}/images/services/monstrance.jpg`} alt="Plated finish inspection" />
          </div>
          <p className="muted">Replating of Worn or Thinning Surfaces</p>

          <div className="prose">
            <p>
              Gold and silver plating services are offered when original
              plating has worn thin, become uneven, or failed due to age and
              use. Surface preparation is handled conservatively to preserve
              underlying detail and geometry.
            </p>

            <p>
              Plating services are commonly requested for chalices and patens
              with worn interiors, ciboria with thinning gold surfaces, and
              silvered vessels with uneven loss or discoloration.
            </p>

            <p>
              All replated surfaces are inspected for uniformity, adhesion,
              and finish consistency prior to return.
            </p>
          </div>
        </div>
      </section>

      {/* ================= PRESERVATION ================= */}
      <section className="section paper">
        <div className="container narrow">

          <h2>Preservation</h2>
          <div className="service-image-row">
            <img src={`${ASSET_URL}/images/services/tabernacle.jpg`} alt="Protective lacquer application" />
            <img src={`${ASSET_URL}/images/services/door.jpg`} alt="Finished preserved surface" />
            <img src={`${ASSET_URL}/images/services/candlestick.jpg`} alt="Finished preserved surface" />
          </div>
          <p className="muted">Protective Lacquer</p>

          <div className="prose">
            <p>
              In appropriate cases, a clear protective lacquer may be applied
              to slow future tarnishing and reduce handling wear. This service
              is typically recommended for items that are infrequently used,
              displayed, or stored in environments prone to oxidation.
            </p>

            <p>
              Lacquer application is discussed on a case-by-case basis and is
              never applied where it would interfere with liturgical function,
              appearance, or future restoration.
            </p>
          </div>
        </div>
      </section>

      {/* ================= KNEELER UPHOLSTERY ================= */}
      <section className="section stone">
        <div className="container narrow">

          <h2>Kneeler Upholstery</h2>
          <div className="service-image-row">
            <img src={`${ASSET_URL}/images/services/kneeler-1.jpg`} alt="Kneeler upholstery repair" />
            <img src={`${ASSET_URL}/images/services/kneeler-2.jpg`} alt="Kneeler padding replacement" />
            <img src={`${ASSET_URL}/images/services/kneeler-3.jpg`} alt="Kneeler padding replacement" />
          </div>
          <p className="muted">Repair and Reupholstery of Liturgical Furnishings</p>

          <div className="prose">
            <p>
              Kneeler upholstery services address worn padding, damaged fabric,
              or structural concerns. Work is performed with attention to
              durability, comfort, and visual compatibility with the
              surrounding sanctuary.
            </p>

            <p>
              Services may include padding replacement, fabric repair or
              replacement, and minor structural corrections. This service is
              often requested alongside metal restoration as part of
              coordinated sanctuary upkeep.
            </p>
          </div>
        </div>
      </section>

      {/* ================= REPAIR & RE-MANUFACTURING ================= */}
      <section className="section paper">
        <div className="container narrow">

          <h2>Repair &amp; Re-Manufacturing</h2>
          <div className="service-image-row">
            <img src={`${ASSET_URL}/images/services/mechanism.jpg`} alt="Structural repair detail" />
            <img src={`${ASSET_URL}/images/services/thurible.jpg`} alt="Component re-manufacturing detail" />
            <img src={`${ASSET_URL}/images/services/lunette.jpg`} alt="Component re-manufacturing detail" />
          </div>
          <p className="muted">Structural Repair and Component Replacement</p>

          <div className="prose">
            <p>
              For items with mechanical issues, missing components, or
              structural damage, we offer repair and limited
              re-manufacturing services.
            </p>

            <p>
              When replacement is necessary, new components are fabricated to
              match original design, materials, and proportions as closely as
              possible. All repair work is evaluated carefully to ensure it
              respects the original intent and construction of the piece.
            </p>
          </div>
        </div>
      </section>

      {/* ================= CTA ================= */}
      <section className="section stone">
        <div className="container center narrow">
          <h2>Request a Restoration Evaluation</h2>
          <p className="muted">
            Each piece is reviewed individually to determine condition and
            appropriate treatment. We are happy to review photographs and
            details before recommending next steps.
          </p>

          <a href="/contact" className="btn primary">
            Request an Evaluation
          </a>
        </div>
      </section>
    </>
  );
}